/**
 * Lookup district name and state code by Indian pincode.
 *
 * Data sources:
 * - pincode-to-district-state.json: { [pincode]: { district, stateCode } } where stateCode is State Id (1–38)
 * - kra-to-state.json: { [stateId]: stateCode2Letter } e.g. { "33": "DL" }
 */
import stateIdToCode from './kra-to-state.json';
import pincodeMapping from './pincode-to-district-state.json';

export type PincodeLookup = {
  district: string;
  /** Two-letter state code (e.g. DL, MH, KA) resolved from State Master */
  stateCode: string;
  /** Raw numeric State Id from PINCODE master (if needed for backend) */
  stateId: string;
};

type RawEntry = {
  district: string;
  /** State Id from PINCODE District Master (1–38) */
  stateCode: string;
};

const mapping = pincodeMapping as Record<string, RawEntry>;
const stateIdMap = stateIdToCode as Record<string, string>;

/**
 * Returns district name, resolved 2-letter state code, and raw State Id
 * for a given pincode, or null if not found.
 * Pincode is normalized (trimmed, stringified) before lookup.
 */
export function getDistrictAndStateByPincode(
  pincode: string | null | undefined
): PincodeLookup | null {
  if (pincode == null || String(pincode).trim() === '') return null;
  const key = String(pincode).trim();
  const raw = mapping[key];
  if (!raw) return null;

  const stateId = raw.stateCode;
  const resolvedStateCode = stateIdMap[stateId] ?? '';

  return {
    district: raw.district,
    stateCode: resolvedStateCode,
    stateId,
  };
}

/**
 * All unique districts from the mapping (for building dropdown options if needed).
 */
export function getUniqueDistricts(): { value: string; label: string }[] {
  const seen = new Set<string>();
  const out: { value: string; label: string }[] = [];
  for (const entry of Object.values(mapping)) {
    if (entry.district && !seen.has(entry.district)) {
      seen.add(entry.district);
      out.push({ value: entry.district, label: entry.district });
    }
  }
  return out.sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * State dropdown options where value is the numeric State Id (for API stateCode)
 * and label is the state name. Uses kra-to-state + INDIAN_STATES for labels.
 */
export function getStateOptionsWithNumericValue(
  stateNameBy2Letter: Record<string, string>
): { value: string; label: string }[] {
  return Object.entries(stateIdMap)
    .map(([stateId, twoLetter]) => ({
      value: stateId,
      label: stateNameBy2Letter[twoLetter] ?? twoLetter,
    }))
    .filter(o => o.label)
    .sort((a, b) => a.label.localeCompare(b.label));
}
